import { Component, OnInit } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { AuthConfigs } from '../../../../core/models/authenticationConfig';
import {
  AuthType, AzureAuth, LDAPAuth, OKTAAuth, OpenIdAuth, SyncHpcAPIAuth, saml,
  SamlAuth,
  PingIdentityAuth,
} from '../../../../core/models/globals';
import { AuthenticationService } from '../../../../core/services/authentication.service';

@Component({
  selector: 'app-update-authentication-configuration',
  templateUrl: './update-authentication-configuration.component.html',
  styleUrls: ['./update-authentication-configuration.component.scss']
})
export class UpdateAuthenticationConfigurationComponent implements OnInit {

  constructor(
    private dialog: MatDialogRef<UpdateAuthenticationConfigurationComponent>,
    private snackBar: MatSnackBar,
    private authenticationService: AuthenticationService
  ) { }

  syncHpcAPIAuthDetails: UntypedFormGroup;
  ldapAuthDetails: UntypedFormGroup;
  azureADAuthDetails: UntypedFormGroup;
  oktaAuthDetails: UntypedFormGroup;
  authTypeDetails: UntypedFormGroup;
  openIdAuthDetails: UntypedFormGroup;
  samlAuthDetails: UntypedFormGroup;
  pingAuthDetails: UntypedFormGroup;

  isLinear = false;
  returnValues;
  authType = this.authenticationService.getAuthenticationType();
  //authType = 0

  //syncHpcApiAuthValues: SyncHpcAPIAuth;

  ngOnInit(): void {
    this.getAuthenticationValues();
    this.syncHpcAPIAuthDetails = new UntypedFormGroup({
      identityServer: new UntypedFormControl(),
      identityServerPort: new UntypedFormControl(),
    })
    this.ldapAuthDetails = new UntypedFormGroup({
      enterpriseADServer: new UntypedFormControl(),
      enterpriseDomainName: new UntypedFormControl(),
    })
    this.azureADAuthDetails = new UntypedFormGroup({
      clientID: new UntypedFormControl(),
      authority: new UntypedFormControl(),
      redirectUri: new UntypedFormControl(),
      cacheLocation: new UntypedFormControl(),
      storeAuthStateInCookie: new UntypedFormControl(),
      protectedResourceMap: new UntypedFormControl(),
      azureADMetadataEndpoint: new UntypedFormControl(),
      azureADClientSecret: new UntypedFormControl(),
      azureADTenantID: new UntypedFormControl(),
    })
    this.oktaAuthDetails = new UntypedFormGroup({
      issuer: new UntypedFormControl(),
      clientId: new UntypedFormControl(),
      redirectUri: new UntypedFormControl(),
      scopes: new UntypedFormControl(),
      oktaMetadataEndpoint: new UntypedFormControl()
    })
    this.openIdAuthDetails = new UntypedFormGroup({
      domain: new UntypedFormControl(),
      clientId: new UntypedFormControl(),
      audience: new UntypedFormControl(),
      clientSecret: new UntypedFormControl()
    })
    this.samlAuthDetails = new UntypedFormGroup({
      idPMetadataURL: new UntypedFormControl(),
      issuer: new UntypedFormControl(),
    });
    this.authTypeDetails = new UntypedFormGroup({
      authType: new UntypedFormControl(this.authType.toString())
    });
    this.pingAuthDetails = new UntypedFormGroup({
      publicClientId: new UntypedFormControl(),
      confidentialClientId: new UntypedFormControl(),
      confidentialClientSecret: new UntypedFormControl(),
      issuer: new UntypedFormControl(),
      tokenEndpoint: new UntypedFormControl(),
      redirectUri: new UntypedFormControl(),
      introspectionEndpoint: new UntypedFormControl(),
      discoveryEndpoint: new UntypedFormControl(),
      scope: new UntypedFormControl('openid profile email'),
      responseType: new UntypedFormControl('code'),
      showDebugInformation: new UntypedFormControl(true),
      disablePKCE: new UntypedFormControl(false),
    });

    this.optionClicked(this.authType?.toString())
  }

  onClose() {
    this.dialog.close(false);
  }

  onSubmit(syncHpcAPIAuthDetails: UntypedFormGroup, ldapAuthDetails: UntypedFormGroup,
    azureADAuthDetails: UntypedFormGroup, oktaAuthDetails: UntypedFormGroup,
    samlAuthDetails: UntypedFormGroup, pingAuthDetails: UntypedFormGroup,) {

    if (!syncHpcAPIAuthDetails.valid) {
      this.syncHpcAPIAuthDetails.markAllAsTouched();
      this.showError();
      return;
    }
    if (!ldapAuthDetails.valid) {
      this.ldapAuthDetails.markAllAsTouched();
      this.showError();
      return;
    }
    if (!azureADAuthDetails.valid) {
      this.azureADAuthDetails.markAllAsTouched();
      this.showError();
      return;
    }
    if (!oktaAuthDetails.valid) {
      this.oktaAuthDetails.markAllAsTouched();
      this.showError();
      return;
    }

    if (!samlAuthDetails.valid) {
      this.samlAuthDetails.markAllAsTouched();
      this.showError();
      return;
    }

    if (!pingAuthDetails.valid) {
      this.pingAuthDetails.markAllAsTouched();
      this.showError();
      return;
    }


    let syncHpcApiAuthValues: SyncHpcAPIAuth = {
      identityServer: syncHpcAPIAuthDetails.value.identityServer,
      identityServerPort: syncHpcAPIAuthDetails.value.identityServerPort
    }

    let ldapAuthValues: LDAPAuth = {
      enterpriseADServer: ldapAuthDetails.value.enterpriseADServer,
      enterpriseDomainName: ldapAuthDetails.value.enterpriseDomainName
    }

    let azureAuthValues: AzureAuth = {
      clientID: azureADAuthDetails.value.clientID,
      authority: azureADAuthDetails.value.authority,
      redirectUri: azureADAuthDetails.value.redirectUri,
      cacheLocation: azureADAuthDetails.value.cacheLocation,
      storeAuthStateInCookie: Boolean(JSON.parse(azureADAuthDetails.value.storeAuthStateInCookie)),
      protectedResourceMap: azureADAuthDetails.value.protectedResourceMap,
      azureADMetadataEndpoint: azureADAuthDetails.value.azureADMetadataEndpoint,
      azureADClientSecret: azureADAuthDetails.value.azureADClientSecret,
      azureADTenantID: azureADAuthDetails.value.azureADTenantID
    }

    let oktaAuthValues: OKTAAuth = {
      issuer: oktaAuthDetails.value.issuer,
      clientId: oktaAuthDetails.value.clientId,
      redirectUri: oktaAuthDetails.value.redirectUri,
      scopes: ((oktaAuthDetails.value.scopes as unknown) as string)?.split(','),
      oktaMetadataEndpoint: oktaAuthDetails.value.oktaMetadataEndpoint
    }

    let openIdAuthValues: OpenIdAuth = {
      domain: this.openIdAuthDetails.value.domain,
      clientId: this.openIdAuthDetails.value.clientId,
      audience: this.openIdAuthDetails.value.audience,
      clientSecret: this.openIdAuthDetails.value.clientSecret
    }

    let samlDet: saml = {
      idPMetadata: this.samlAuthDetails.value.idPMetadataURL,
      issuer: this.samlAuthDetails.value.issuer,
      signatureAlgorithm: "http://www.w3.org/2001/04/xmldsig-more#rsa-sha256",
      certificateValidationMode: "None",
      revocationMode: "NoCheck",
    };

    let samlAuth: SamlAuth = {
      saml2: samlDet,
    };

    let pingAuthValues: PingIdentityAuth = {
      publicClientId: this.pingAuthDetails.value.publicClientId,
      confidentialClientId: this.pingAuthDetails.value.confidentialClientId,
      confidentialClientSecret: this.pingAuthDetails.value.confidentialClientSecret,
      issuer: this.pingAuthDetails.value.issuer,
      tokenEndpoint: this.pingAuthDetails.value.tokenEndpoint,
      introspectionEndpoint: this.pingAuthDetails.value.introspectionEndpoint,
      redirectUri: this.pingAuthDetails.value.redirectUri,
      discoveryEndpoint: this.pingAuthDetails.value.discoveryEndpoint,
      scope: this.pingAuthDetails.value.scope,
      responseType: this.pingAuthDetails.value.responseType,
      showDebugInformation: this.pingAuthDetails.value.showDebugInformation,
      disablePKCE: this.pingAuthDetails.value.disablePKCE,
    };

    let authTypeValues = this.authTypeDetails.value.authType;

    let authConfigs: AuthConfigs = {
      syncHpcApi: syncHpcApiAuthValues,
      azureAuth: azureAuthValues,
      ldapAuth: ldapAuthValues,
      oktaAuth: oktaAuthValues,
      openIdAuth: openIdAuthValues,
      authType: authTypeValues,
      samlAuth: samlDet,
      pingIdentity: pingAuthValues
    }

    this.authenticationService.updateAuthenticationConfig(authConfigs).subscribe({
      next: (data) => {
        if (data) {
          this.snackBar.open("Authenticatoin configuration updated successfully.", "Ok", { duration: 5000 });
          this.dialog.close(true);
        }
      }, error: (err) => {
        this.snackBar.open("Authenticatoin configuration could not be updated.", "Ok", { duration: 5000 });
        this.dialog.close(false);
      }
    })
  }

  getAuthenticationValues() {
    this.authenticationService.getAuthenticationValues().subscribe({
      next: (data) => {
        if (data) {
          this.returnValues = JSON.parse(JSON.stringify(data));
          if (this.returnValues.SyncHPCAPI) {
            this.syncHpcAPIAuthDetails.controls['identityServer'].setValue(this.returnValues.SyncHPCAPI.authParams.identityServer);
            this.syncHpcAPIAuthDetails.controls['identityServerPort'].setValue(this.returnValues.SyncHPCAPI.authParams.identityServerPort);
          }

          if (this.returnValues.LDAP) {
            this.ldapAuthDetails.controls['enterpriseADServer'].setValue(this.returnValues.LDAP.authParams.enterpriseADServer);
            this.ldapAuthDetails.controls['enterpriseDomainName'].setValue(this.returnValues.LDAP.authParams.enterpriseDomainName);
          }

          if (this.returnValues.AzureAD) {
            this.azureADAuthDetails.controls['clientID'].setValue(this.returnValues.AzureAD.authParams.clientID);
            this.azureADAuthDetails.controls['authority'].setValue(this.returnValues.AzureAD.authParams.authority);
            this.azureADAuthDetails.controls['redirectUri'].setValue(this.returnValues.AzureAD.authParams.redirectUri);
            this.azureADAuthDetails.controls['cacheLocation'].setValue(this.returnValues.AzureAD.authParams.cacheLocation);
            this.azureADAuthDetails.controls['storeAuthStateInCookie'].setValue(this.returnValues.AzureAD.authParams.storeAuthStateInCookie?.toString());
            this.azureADAuthDetails.controls['protectedResourceMap'].setValue(this.returnValues.AzureAD.authParams.protectedResourceMap);
            this.azureADAuthDetails.controls['azureADMetadataEndpoint'].setValue(this.returnValues.AzureAD.authParams.azureADMetadataEndpoint);
            this.azureADAuthDetails.controls['azureADClientSecret'].setValue(this.returnValues.AzureAD.authParams.azureADClientSecret);
            this.azureADAuthDetails.controls['azureADTenantID'].setValue(this.returnValues.AzureAD.authParams.azureADTenantID);
          }

          if (this.returnValues.Okta) {
            this.oktaAuthDetails.controls['issuer'].setValue(this.returnValues.Okta.authParams.issuer);
            this.oktaAuthDetails.controls['clientId'].setValue(this.returnValues.Okta.authParams.clientId);
            this.oktaAuthDetails.controls['redirectUri'].setValue(this.returnValues.Okta.authParams.redirectUri);
            this.oktaAuthDetails.controls['scopes'].setValue(this.returnValues.Okta.authParams.scopes?.toString());
            this.oktaAuthDetails.controls['oktaMetadataEndpoint'].setValue(this.returnValues.Okta.authParams.oktaMetadataEndpoint);
          }

          if (this.returnValues.OpenId) {
            this.openIdAuthDetails.controls['domain'].setValue(this.returnValues.OpenId.authParams.domain);
            this.openIdAuthDetails.controls['clientId'].setValue(this.returnValues.OpenId.authParams.clientId);
            this.openIdAuthDetails.controls['audience'].setValue(this.returnValues.OpenId.authParams.audience);
            this.openIdAuthDetails.controls['clientSecret'].setValue(this.returnValues.OpenId.authParams.clientSecret);
          }

          if (this.returnValues.SAML) {
            this.samlAuthDetails.controls["idPMetadataURL"].setValue(
              this.returnValues.SAML.authParams.Saml2.IdPMetadata
            );
            this.samlAuthDetails.controls["issuer"].setValue(
              this.returnValues.SAML.authParams.Saml2.Issuer
            );
          }
          if (this.returnValues.PingIdentity) {
            this.pingAuthDetails.setValue(this.returnValues.PingIdentity.authParams);
          }
        
        }
        else {
          this.snackBar.open("Error occurred while getting authentication values.", "Ok", { duration: 5000 });
        }
      }, error: (err) => {
        this.snackBar.open("Failed to get authentication configuration.", 'Ok', {
          duration: 5000
        })
      }
    })
  }

  optionClicked(value: string) {
    this.removeValidators(this.ldapAuthDetails);
    this.removeValidators(this.azureADAuthDetails);
    this.removeValidators(this.syncHpcAPIAuthDetails);
    this.removeValidators(this.oktaAuthDetails);
    this.removeValidators(this.samlAuthDetails);
    this.removeValidators(this.pingAuthDetails);

    switch (parseInt(value)) {
      case AuthType.SyncHPCAPI:
        this.syncHpcAPIAuthDetails.controls['identityServer'].setValidators(Validators.required);
        this.syncHpcAPIAuthDetails.controls['identityServerPort'].setValidators(Validators.required);
        this.syncHpcAPIAuthDetails.markAllAsTouched();
        this.updateValueAndValidityOfFromControls(this.syncHpcAPIAuthDetails);
        break;

      case AuthType.LDAP:
        this.ldapAuthDetails.controls['enterpriseADServer'].setValidators(Validators.required);
        this.ldapAuthDetails.controls['enterpriseDomainName'].setValidators(Validators.required);
        this.ldapAuthDetails.markAllAsTouched();
        this.updateValueAndValidityOfFromControls(this.ldapAuthDetails);
        break;

      case AuthType.AzureAD:
        this.azureADAuthDetails.controls['clientID'].setValidators(Validators.required);
        this.azureADAuthDetails.controls['authority'].setValidators(Validators.required);
        this.azureADAuthDetails.controls['redirectUri'].setValidators(Validators.required);
        this.azureADAuthDetails.controls['cacheLocation'].setValidators(Validators.required);
        this.azureADAuthDetails.controls['storeAuthStateInCookie'].setValidators(Validators.required);
        this.azureADAuthDetails.controls['protectedResourceMap'].setValidators(Validators.required);
        this.azureADAuthDetails.controls['azureADMetadataEndpoint'].setValidators(Validators.required);
        this.azureADAuthDetails.controls['azureADClientSecret'].setValidators(Validators.required);
        this.azureADAuthDetails.controls['azureADTenantID'].setValidators(Validators.required);
        this.azureADAuthDetails.markAllAsTouched();
        this.updateValueAndValidityOfFromControls(this.azureADAuthDetails);
        break;

      case AuthType.Okta:
        this.oktaAuthDetails.controls['issuer'].setValidators(Validators.required);
        this.oktaAuthDetails.controls['clientId'].setValidators(Validators.required);
        this.oktaAuthDetails.controls['redirectUri'].setValidators(Validators.required);
        this.oktaAuthDetails.controls['scopes'].setValidators(Validators.required);
        this.oktaAuthDetails.controls['oktaMetadataEndpoint'].setValidators(Validators.required);
        this.oktaAuthDetails.markAllAsTouched();
        this.updateValueAndValidityOfFromControls(this.oktaAuthDetails);
        break;

      case AuthType.PingIdentity:
        this.pingAuthDetails.controls['clientId'].setValidators(Validators.required);
        this.pingAuthDetails.controls['clientSecret'].setValidators(Validators.required);
        this.pingAuthDetails.controls['issuer'].setValidators(Validators.required);
        this.pingAuthDetails.controls['tokenEndpoint'].setValidators(Validators.required);
        this.pingAuthDetails.controls['redirectUri'].setValidators(Validators.required);
        this.pingAuthDetails.controls['discoveryEndpoint'].setValidators(Validators.required);
        this.pingAuthDetails.controls['scope'].setValidators(Validators.required);
        this.pingAuthDetails.controls['responseType'].setValidators(Validators.required);
        this.pingAuthDetails.controls['showDebugInformation'].setValidators(Validators.required);
        this.pingAuthDetails.controls['disablePKCE'].setValidators(Validators.required);

        this.pingAuthDetails.markAllAsTouched();
        this.updateValueAndValidityOfFromControls(this.pingAuthDetails);
        break;

      case AuthType.SamlId:
        this.samlAuthDetails.controls['idPMetadataURL'].setValidators(Validators.required);
        this.oktaAuthDetails.controls['issuer'].setValidators(Validators.required);

      default:
        this.removeValidators(this.ldapAuthDetails);
        this.removeValidators(this.azureADAuthDetails);
        this.removeValidators(this.oktaAuthDetails);
        break;
    }
  }

  public removeValidators(form: UntypedFormGroup) {
    for (const key in form.controls) {
      form.get(key).clearValidators();
      form.get(key).updateValueAndValidity();
    }
  }

  showError() {
    this.snackBar.open('Please fill / Verify all the required fields !', 'Ok', {
      duration: 5000,
    });
  }

  updateValueAndValidityOfFromControls(form: UntypedFormGroup) {
    for (const key in form.controls) {
      form.get(key).updateValueAndValidity();
    }
  }
}
